// common/FilterMenu.js
// This will be used to pass filter functions and options to the API
import { useState } from "react";
import { DelDatePicker } from "./DelDatePicker";
import Select, { createFilter } from "react-select";
import VirtualizedList from "./virtualList";
import styles from "./FilterMenu.module.css";
import { filterSelectStyles } from "./delSelect";

// Select for projects
/**
 *
 * @param {Array} allProjectInfo - Array of all project information passed from appStore through the main delList component
 * @param {Array} allEmployees - Array of all employee positions passed from appStore through the main delList component
 * @returns
 */
export const ProjectSelectionInput = ({
  allProjectInfo,
  allInactiveProjects,
  selectedProjectValue,
  selectedProjectLabel,
  selectedProjectPhaseList,
  setSelectedProjectFunction,
}) => {
  // Setting up a menu list component with the virtualized list component
  const MenuList = ({ children, maxHeight, dataTestId }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42; // 8px top padding + 8px bottom padding + 14px font + line height
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
        className={dataTestId}
      />
    );
  };

  const [showInactiveProjects, setShowInactiveProjects] = useState(false);

  const handleShowInactiveProjects = () => {
    setShowInactiveProjects(!showInactiveProjects);
  };

  return (
    <>
      <div className={styles.showInactiveProjectsContainer}>
        <input
          type="checkbox"
          checked={showInactiveProjects}
          onChange={handleShowInactiveProjects}
        />
        <label>Show Inactive Projects</label>
      </div>
      <Select
        classNamePrefix="project-search-input"
        value={
          selectedProjectValue
            ? {
                value: selectedProjectValue,
                label: selectedProjectLabel,
                phase_list: selectedProjectPhaseList,
              }
            : "Search Active Projects"
        }
        onChange={setSelectedProjectFunction}
        options={
          showInactiveProjects
            ? allInactiveProjects.map((project) => ({
                value: project.value,
                label: project.label,
                phase_list: project.phase_list,
              }))
            : allProjectInfo.map((project) => ({
                value: project.value,
                label: project.label,
                phase_list: project.phase_list,
              }))
        }
        isSearchable={true}
        isClearable={true}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        placeholder={"Search Active Projects"}
        styles={filterSelectStyles}
        filterOptions={createFilter({ ignoreAccents: false })}
        components={{
          MenuList: (props) => (
            <MenuList {...props} dataTestId="project-search-result" />
          ),
        }}
      />
    </>
  );
};

// Select for phases
/**
 * @param {Array} projectPhases - Array of all phases passed from selectedProject
 * @param {Array} selectedPhase - The selected phase passed from selectedProject
 * @param {Function} setSelectedPhaseFunction - Function to set the selected phase passed from main filterMenu component
 */
export const PhaseSelectionInput = ({
  projectPhases,
  selectedPhaseValue,
  selectedPhaseLabel,
  setSelectedPhaseFunction,
}) => {
  const PhaseMenuList = ({ children, maxHeight, dataTestId }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42; // 8px top padding + 8px bottom padding + 14px font + line height
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
        className={dataTestId}
      />
    );
  };

  return (
    <>
      <Select
        classNamePrefix="phase-search-input"
        value={
          selectedPhaseValue
            ? {
                value: selectedPhaseValue,
                label: selectedPhaseLabel,
              }
            : "Select Phase"
        }
        onChange={setSelectedPhaseFunction}
        isDisabled={projectPhases?.length === 0}
        options={projectPhases}
        placeholder={
          selectedPhaseValue ? (
            <span className={styles.errorText}>{selectedPhaseValue}</span>
          ) : projectPhases?.length === 0 ? (
            "Select a project first"
          ) : projectPhases?.length > 0 ? (
            "Select Phase"
          ) : (
            "No Phases For This Project"
          )
        }
        noOptionsMessage={() =>
          projectPhases?.length === 0
            ? "No phases available"
            : "Select a project first"
        }
        styles={filterSelectStyles}
        isClearable={true}
        isSearchable={true}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        components={{
          MenuList: (props) => (
            <PhaseMenuList {...props} dataTestId="phase-search-result" />
          ),
        }}
      />
    </>
  );
};

export const SearchEmployeeInput = ({
  allEmployees,
  helpText,
  selectedEmployeeValue,
  selectedEmployeeLabel,
  setSelectedEmployeeFunction,
}) => {
  const EmployeeMenuList = ({ children, maxHeight, dataTestId }) => {
    const itemCount = Array.isArray(children) ? children.length : 1;
    const itemHeight = 42; // 8px top padding + 8px bottom padding + 14px font + line height
    const listHeight = Math.min(maxHeight, itemCount * itemHeight);
    return (
      <VirtualizedList
        items={Array.isArray(children) ? children : [children]}
        height={listHeight}
        width="100%"
        itemHeight={itemHeight}
        renderItem={(child) => child}
        className={dataTestId}
      />
    );
  };

  return (
    <>
      <Select
        classNamePrefix="employee-search-input"
        value={
          selectedEmployeeValue
            ? {
                value: selectedEmployeeValue,
                label: selectedEmployeeLabel,
              }
            : "Search Employees"
        }
        onChange={setSelectedEmployeeFunction}
        options={allEmployees.map((employee) => ({
          value: employee.employee_id,
          label: employee.label,
        }))}
        isSearchable={true}
        isClearable={true}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        placeholder={helpText}
        styles={filterSelectStyles}
        components={{
          MenuList: (props) => (
            <EmployeeMenuList {...props} dataTestId="employee-search-result" />
          ),
        }}
      />
    </>
  );
};

export const DelStatusSelectionInput = ({
  selectedDelStatusValue,
  selectedDelStatusLabel,
  setSelectedDelStatusFunction,
  options = null,
}) => {
  const statusOptions = options || [
    { value: "", label: "All" },
    { value: "1", label: "Submitted" },
    { value: "2", label: "Approved" },
  ];
  return (
    <Select
      classNamePrefix="del-status-search-input"
      value={
        selectedDelStatusValue
          ? {
              value: selectedDelStatusValue,
              label: selectedDelStatusLabel,
            }
          : "Select Del Status"
      }
      onChange={setSelectedDelStatusFunction}
      options={statusOptions}
      isSearchable={true}
      isClearable={true}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      styles={filterSelectStyles}
    />
  );
};

export const OrgSelectionInput = ({
  listOfOrgs,
  selectedOrgValue,
  selectedOrgLabel,
  setSelectedOrgFunction,
}) => {
  return (
    <Select
      classNamePrefix="org-search-input"
      value={
        selectedOrgValue
          ? { value: selectedOrgValue, label: selectedOrgLabel }
          : "Select Org"
      }
      onChange={setSelectedOrgFunction}
      options={listOfOrgs}
      isSearchable={true}
      isClearable={true}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      placeholder="Select Org"
      styles={filterSelectStyles}
    />
  );
};

// Filter Menu for the DEL Tabs
/**
 * @param {String} delTab - The tab that the user is on, options: "drafts", "submitted", "approvals"
 * @param {Object} filters - The filters object from the specific delList
 * @param {Object} filterActions - The filter actions object from the specific delList
 * @param {Array} delStatusOptions - Array of options and values such as [{value:"", label:"All"}, {value:0, label: "Draft"}, {value:1, label: "Submitted"}, {value:2, label:Approved}].
 * @param {Array} filterPhaseList - Array of all phases passed from selectedProject
 * @param {Function} filterPhaseActionFunction - Function to set the selected phase passed from main filterMenu component
 * @param {Function} updateFilterAction - Function to update the filter passed from main filterMenu component
 * @param {Function} onApplyFilters - Callback for when filters are applied (triggers fetch)
 * @param {Array} allProjectInfo - Array of all project information passed from appStore through the main delList component
 * @param {Array} allEmployees - Array of all employee positions passed from appStore through the main delList component
 */
export const DelFilterMenu = ({
  delTab,
  filters, // Pass filters state from the specific delList
  delStatusOptions = null,
  filterPhaseList,
  filterPhaseActionFunction,
  updateFilterAction, // Specifc action to update a filter
  onApplyFilters, // Callback for when filters are applied (triggers fetch)
  onClearFilters,
  allProjectInfo,
  allInactiveProjects,
  allEmployees,
}) => {
  const handleFilterChange = (field, value) => {
    updateFilterAction({ [field]: value });
  };

  // Handle setSelectedProject
  const handleSetSelectedProject = (project) => {
    if (!project) {
      handleFilterChange("projectNumber", "");
      handleFilterChange("projectLabel", "");
      filterPhaseActionFunction([]);
      return;
    }
    filterPhaseActionFunction(project.phase_list);
    handleFilterChange("projectNumber", project.value);
    handleFilterChange("projectLabel", project.label);
    // Making the phase section blank due to new project selection
    handleFilterChange("phase", "");
    handleFilterChange("phaseLabel", "");
  };

  // Handle setSelectedPhase
  const handleSetSelectedPhase = (phase) => {
    if (!phase) {
      handleFilterChange("phase", "");
      handleFilterChange("phaseLabel", "");
      return;
    }
    handleFilterChange("phase", phase.phase_id);
    handleFilterChange("phaseLabel", phase.label);
  };

  // Handle setSelectedCreatedBy
  const handleSetSelectedCreatedBy = (employee) => {
    if (!employee) {
      handleFilterChange("createdBy", "");
      handleFilterChange("createdByName", "");
      return;
    }
    handleFilterChange("createdBy", employee.value);
    handleFilterChange("createdByName", employee.label);
  };

  // Handle setSelectedProjectManager
  const handleSetSelectedProjectManager = (projectManager) => {
    if (!projectManager) {
      handleFilterChange("projectManager", "");
      handleFilterChange("projectManagerName", "");
      return;
    }
    handleFilterChange("projectManager", projectManager.value);
    handleFilterChange("projectManagerName", projectManager.label);
  };

  // Handle setSelectedInvoiceManager
  const handleSetSelectedInvoiceManager = (invoiceManager) => {
    if (!invoiceManager) {
      handleFilterChange("invoiceManager", "");
      handleFilterChange("invoiceManagerName", "");
      return;
    }

    handleFilterChange("invoiceManager", invoiceManager.value);
    handleFilterChange("invoiceManagerName", invoiceManager.label);
  };

  // Handle DatePicker date change
  const handleDatePickerDateChange = (field, date) => {
    // If the date is null, set the field to an empty string
    if (!date) {
      handleFilterChange(field, "");
      return;
    }
    // Making the date into a YYYY-MM-DD
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const formattedDate = `${year}-${month}-${day}`;
    handleFilterChange(field, formattedDate);
  };

  const handleSetSelectedDelStatus = (status) => {
    if (!status) {
      handleFilterChange("status", "");
      handleFilterChange("statusLabel", "");
      return;
    }
    handleFilterChange("status", status.value);
    handleFilterChange("statusLabel", status.label);
  };

  return (
    <div className={styles.reportFilterMenu}>
      <div className={styles.headerContainer}>
        <h3>Filter Options</h3>
      </div>
      <div className={styles.filtersContainer}>
        {delTab === "submitted" && (
          <div className={styles.filterRow}>
            <label htmlFor="delStatus">Del Status:</label>
            <DelStatusSelectionInput
              selectedDelStatusValue={filters.status}
              selectedDelStatusLabel={filters.statusLabel}
              setSelectedDelStatusFunction={handleSetSelectedDelStatus}
              options={delStatusOptions}
            />
          </div>
        )}

        {/* Date filters */}
        <div className={styles.filterRow}>
          <label>Created Date:</label>
          <div className={styles.datePickerContainer}>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>Start:</label>
              <DelDatePicker
                placeHolderText=""
                selected={filters?.createdStartDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("createdStartDate", date)
                }
                selectsStart
                startDate={filters?.createdStartDate || ""}
                endDate={filters?.createdEndDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
                maxDate={filters?.createdEndDate || ""}
              />
            </div>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>End:</label>
              <DelDatePicker
                placeHolderText=""
                selected={filters?.createdEndDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("createdEndDate", date)
                }
                selectsEnd
                startDate={filters?.createdStartDate || ""}
                endDate={filters?.createdEndDate || ""}
                minDate={filters?.createdStartDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
              />
            </div>
          </div>
        </div>
        <div className={styles.filterRow}>
          <label>Del Date:</label>
          <div className={styles.datePickerContainer}>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>Start:</label>
              <DelDatePicker
                placeHolderText=""
                selected={filters?.delStartDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("delStartDate", date)
                }
                selectsStart
                startDate={filters?.delStartDate || ""}
                endDate={filters?.delEndDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
                maxDate={filters?.delEndDate || ""}
              />
            </div>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>End:</label>
              <DelDatePicker
                placeHolderText=""
                selected={filters?.delEndDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("delEndDate", date)
                }
                selectsEnd
                startDate={filters?.delStartDate || ""}
                endDate={filters?.delEndDate || ""}
                minDate={filters?.delStartDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
              />
            </div>
          </div>
        </div>

        {delTab !== "drafts" && (
          <div className={styles.filterRow}>
            <label>Submit Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.submitStartDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("submitStartDate", date)
                  }
                  selectsStart
                  startDate={filters?.submitStartDate || ""}
                  endDate={filters?.submitEndDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.submitEndDate || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.submitEndDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("submitEndDate", date)
                  }
                  selectsEnd
                  startDate={filters?.submitStartDate || ""}
                  endDate={filters?.submitEndDate || ""}
                  minDate={filters?.submitStartDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>
        )}

        {delTab !== "approvals" && delTab !== "drafts" && (
          <div className={styles.filterRow}>
            <label>Approval Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.approvalStartDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("approvalStartDate", date)
                  }
                  selectsStart
                  startDate={filters?.approvalStartDate || ""}
                  endDate={filters?.approvalEndDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.approvalEndDate || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.approvalEndDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("approvalEndDate", date)
                  }
                  selectsEnd
                  startDate={filters?.approvalStartDate || ""}
                  endDate={filters?.approvalEndDate || ""}
                  minDate={filters?.approvalStartDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>
        )}

        {/* Text input filters */}
        <div className={styles.filterRow}>
          <ProjectSelectionInput
            allProjectInfo={allProjectInfo}
            allInactiveProjects={allInactiveProjects}
            selectedProjectValue={filters?.projectNumber}
            selectedProjectLabel={filters?.projectLabel}
            selectedProjectPhaseList={filters?.projectPhaseList}
            setSelectedProjectFunction={(project) =>
              handleSetSelectedProject(project)
            }
          />
        </div>

        <div className={styles.filterRow}>
          <PhaseSelectionInput
            projectPhases={filterPhaseList}
            selectedPhaseValue={filters?.phase}
            selectedPhaseLabel={filters?.phaseLabel}
            setSelectedPhaseFunction={handleSetSelectedPhase}
          />
        </div>

        <div className={styles.filterRow}>
          <label htmlFor="projectManager">Project Manager:</label>
          <SearchEmployeeInput
            allEmployees={allEmployees}
            selectedEmployeeValue={filters?.projectManager}
            selectedEmployeeLabel={filters?.projectManagerName}
            setSelectedEmployeeFunction={handleSetSelectedProjectManager}
          />
        </div>

        <div className={styles.filterRow}>
          <label htmlFor="invoiceManager">Invoice Manager:</label>
          <SearchEmployeeInput
            allEmployees={allEmployees}
            selectedEmployeeValue={filters?.invoiceManager}
            selectedEmployeeLabel={filters?.invoiceManagerName}
            setSelectedEmployeeFunction={handleSetSelectedInvoiceManager}
          />
        </div>

        <div className={styles.filterRow}>
          <label htmlFor="createdBy">Created By:</label>
          <SearchEmployeeInput
            allEmployees={allEmployees}
            selectedEmployeeValue={filters?.createdBy}
            selectedEmployeeLabel={filters?.createdByName}
            setSelectedEmployeeFunction={handleSetSelectedCreatedBy}
          />
        </div>
      </div>
      <div className={styles.filterActions}>
        <button className={styles.clearButton} onClick={onClearFilters}>
          Clear
        </button>
        <button className={styles.applyButton} onClick={onApplyFilters}>
          Apply
        </button>
      </div>
    </div>
  );
};

export const ReportingFilterMenu = ({
  filters,
  filterActions,
  onApplyFilters,
  onClearFilters,
  allProjectInfo,
  allInactiveProjects,
  allEmployees,
  filterPhaseList,
  filterPhaseActionFunction,
}) => {
  const handleFilterChange = (field, value) => {
    filterActions.updateFilter({ [field]: value });
  };

  // Date normalization to YYYY-MM-DD like DelFilterMenu
  const handleDatePickerDateChange = (field, date) => {
    if (!date) {
      handleFilterChange(field, "");
      return;
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const formattedDate = `${year}-${month}-${day}`;
    handleFilterChange(field, formattedDate);
  };

  // Project select
  const handleSetSelectedProject = (project) => {
    if (!project) {
      handleFilterChange("projectNumber", "");
      handleFilterChange("projectLabel", "");
      filterPhaseActionFunction([]);
      return;
    }
    filterPhaseActionFunction(project.phase_list);
    handleFilterChange("projectNumber", project.value);
    handleFilterChange("projectLabel", project.label);
    handleFilterChange("phase", "");
    handleFilterChange("phaseLabel", "");
  };

  // Phase select
  const handleSetSelectedPhase = (phase) => {
    if (!phase) {
      handleFilterChange("phase", "");
      handleFilterChange("phaseLabel", "");
      return;
    }
    handleFilterChange("phase", phase.phase_id);
    handleFilterChange("phaseLabel", phase.label);
  };

  // Project Manager
  const handleSetSelectedProjectManager = (projectManager) => {
    if (!projectManager) {
      handleFilterChange("projectManager", "");
      handleFilterChange("projectManagerName", "");
      return;
    }
    handleFilterChange("projectManager", projectManager.value);
    handleFilterChange("projectManagerName", projectManager.label);
  };

  // Invoice Manager
  const handleSetSelectedInvoiceManager = (invoiceManager) => {
    if (!invoiceManager) {
      handleFilterChange("invoiceManager", "");
      handleFilterChange("invoiceManagerName", "");
      return;
    }
    handleFilterChange("invoiceManager", invoiceManager.value);
    handleFilterChange("invoiceManagerName", invoiceManager.label);
  };

  // Created By
  const handleSetSelectedCreatedBy = (employee) => {
    if (!employee) {
      handleFilterChange("createdBy", "");
      handleFilterChange("createdByName", "");
      return;
    }
    handleFilterChange("createdBy", employee.value);
    handleFilterChange("createdByName", employee.label);
  };

  // Approved By
  const handleSetSelectedApprovedBy = (employee) => {
    if (!employee) {
      handleFilterChange("approvedBy", "");
      handleFilterChange("approvedByName", "");
      return;
    }
    handleFilterChange("approvedBy", employee.value);
    handleFilterChange("approvedByName", employee.label);
  };

  // Status (reporting uses delStatus keys and includes Draft)
  const handleSetSelectedDelStatus = (status) => {
    if (!status) {
      handleFilterChange("delStatus", "");
      handleFilterChange("delStatusLabel", "");
      return;
    }
    handleFilterChange("delStatus", status.value);
    handleFilterChange("delStatusLabel", status.label);
  };

  return (
    <>
      <div className={styles.reportFilterMenu}>
        <div className={styles.headerContainer}>
          <h3>Filter Options</h3>
        </div>
        <div className={styles.filtersContainer}>
          <div className={styles.filterRow}>
            <label htmlFor="delStatus">Del Status:</label>
            <DelStatusSelectionInput
              selectedDelStatusValue={filters?.delStatus}
              selectedDelStatusLabel={filters?.delStatusLabel}
              setSelectedDelStatusFunction={handleSetSelectedDelStatus}
              options={[
                { value: "", label: "All" },
                { value: "0", label: "Draft" },
                { value: "1", label: "Submitted" },
                { value: "2", label: "Approved" },
              ]}
            />
          </div>

          <div className={styles.filterRow}>
            <label>Created Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.createdStartDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("createdStartDate", date)
                  }
                  selectsStart
                  startDate={filters?.createdStartDate || ""}
                  endDate={filters?.createdEndDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.createdEndDate || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.createdEndDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("createdEndDate", date)
                  }
                  selectsEnd
                  startDate={filters?.createdStartDate || ""}
                  endDate={filters?.createdEndDate || ""}
                  minDate={filters?.createdStartDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>

          <div className={styles.filterRow}>
            <label>Del Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.delStartDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("delStartDate", date)
                  }
                  selectsStart
                  startDate={filters?.delStartDate || ""}
                  endDate={filters?.delEndDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.delEndDate || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.delEndDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("delEndDate", date)
                  }
                  selectsEnd
                  startDate={filters?.delStartDate || ""}
                  endDate={filters?.delEndDate || ""}
                  minDate={filters?.delStartDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>

          <div className={styles.filterRow}>
            <label>Submit Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.submitStartDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("submitStartDate", date)
                  }
                  selectsStart
                  startDate={filters?.submitStartDate || ""}
                  endDate={filters?.submitEndDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.submitEndDate || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.submitEndDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("submitEndDate", date)
                  }
                  selectsEnd
                  startDate={filters?.submitStartDate || ""}
                  endDate={filters?.submitEndDate || ""}
                  minDate={filters?.submitStartDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>

          <div className={styles.filterRow}>
            <label>Approval Date:</label>
            <div className={styles.datePickerContainer}>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>Start:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.approvalStartDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("approvalStartDate", date)
                  }
                  selectsStart
                  startDate={filters?.approvalStartDate || ""}
                  endDate={filters?.approvalEndDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                  maxDate={filters?.approvalEndDate || ""}
                />
              </div>
              <div className={styles.datePickerGroup}>
                <label className={styles.dateLabel}>End:</label>
                <DelDatePicker
                  placeHolderText=""
                  selected={filters?.approvalEndDate || ""}
                  onChange={(date) =>
                    handleDatePickerDateChange("approvalEndDate", date)
                  }
                  selectsEnd
                  startDate={filters?.approvalStartDate || ""}
                  endDate={filters?.approvalEndDate || ""}
                  minDate={filters?.approvalStartDate || ""}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="mm/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
            </div>
          </div>

          <div className={styles.filterRow}>
            <ProjectSelectionInput
              allProjectInfo={allProjectInfo}
              selectedProjectValue={filters?.projectNumber}
              allInactiveProjects={allInactiveProjects}
              selectedProjectLabel={filters?.projectLabel}
              selectedProjectPhaseList={filters?.projectPhaseList}
              setSelectedProjectFunction={(project) =>
                handleSetSelectedProject(project)
              }
            />
          </div>

          <div className={styles.filterRow}>
            <PhaseSelectionInput
              projectPhases={filterPhaseList}
              selectedPhaseValue={filters?.phase}
              selectedPhaseLabel={filters?.phaseLabel}
              setSelectedPhaseFunction={handleSetSelectedPhase}
            />
          </div>

          <div className={styles.filterRow}>
            <label htmlFor="projectManager">Project Manager:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.projectManager}
              selectedEmployeeLabel={filters?.projectManagerName}
              setSelectedEmployeeFunction={handleSetSelectedProjectManager}
            />
          </div>

          <div className={styles.filterRow}>
            <label htmlFor="invoiceManager">Invoice Manager:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.invoiceManager}
              selectedEmployeeLabel={filters?.invoiceManagerName}
              setSelectedEmployeeFunction={handleSetSelectedInvoiceManager}
            />
          </div>

          <div className={styles.filterRow}>
            <label htmlFor="createdBy">Created By:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.createdBy}
              selectedEmployeeLabel={filters?.createdByName}
              setSelectedEmployeeFunction={handleSetSelectedCreatedBy}
            />
          </div>

          <div className={styles.filterRow}>
            <label htmlFor="approvedBy">Approved By:</label>
            <SearchEmployeeInput
              allEmployees={allEmployees}
              selectedEmployeeValue={filters?.approvedBy}
              selectedEmployeeLabel={filters?.approvedByName}
              setSelectedEmployeeFunction={handleSetSelectedApprovedBy}
            />
          </div>
        </div>
        <div className={styles.filterActions}>
          <button className={styles.clearButton} onClick={onClearFilters}>
            Clear
          </button>
          <button className={styles.applyButton} onClick={onApplyFilters}>
            Apply
          </button>
        </div>
      </div>
    </>
  );
};
